import { useListJobs, useGetJobStats, useDeleteJob, useRegenerateJob, getListJobsQueryKey, getGetJobStatsQueryKey } from "@workspace/api-client-react";
import { Link, useLocation, useSearch } from "wouter";
import { Clock, HardDrive, FileAudio, CheckCircle2, AlertCircle, Loader2, PlayCircle, BarChart3, Scissors, Download, TimerOff, Zap, LayoutList, Quote, Ban, Rss, X, Trash2, Check, RefreshCw, Video, AlertTriangle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { format, isPast } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";

const ACTIVE_STATUSES = new Set(["queued", "analyzing", "abbreviating"]);
const BACKFILL_COHORT_WINDOW_MS = 15 * 60 * 1000;
// A queued job that has been sitting for longer than this is surfaced with a
// warning indicator so the user knows it may be waiting behind another job.
const QUEUED_WARN_MS = 5 * 60 * 1000; // 5 minutes

function isActiveStatus(status: string): boolean {
  return ACTIVE_STATUSES.has(status);
}

function formatDuration(seconds: number | null) {
  if (seconds === null) return "--:--";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

function modeIcon(mode: string) {
  switch (mode) {
    case "quick_catchup": return <Zap size={12} />;
    case "key_quotes": return <Quote size={12} />;
    default: return <LayoutList size={12} />;
  }
}

function modeLabel(mode: string) {
  switch (mode) {
    case "quick_catchup": return "Quick";
    case "key_quotes": return "Quotes";
    case "full_recap": return "Recap";
    default: return mode;
  }
}

function isExpired(expiresAt: string | null): boolean {
  if (!expiresAt) return false;
  return isPast(new Date(expiresAt));
}

function getStatusBadge(status: string, expiresAt: string | null) {
  if (status === "ready" && isExpired(expiresAt)) {
    return (
      <Badge variant="outline" className="font-mono text-[10px] uppercase text-muted-foreground border-muted-foreground/30">
        <TimerOff className="mr-1 h-3 w-3" /> Expired
      </Badge>
    );
  }
  switch (status) {
    case "ready":
      return (
        <Badge variant="default" className="bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20 border-emerald-500/20 font-mono text-[10px] uppercase">
          <CheckCircle2 className="mr-1 h-3 w-3" /> Ready
        </Badge>
      );
    case "failed":
      return (
        <Badge variant="destructive" className="font-mono text-[10px] uppercase">
          <AlertCircle className="mr-1 h-3 w-3" /> Failed
        </Badge>
      );
    case "blocked":
      return (
        <Badge variant="outline" className="font-mono text-[10px] uppercase text-amber-500 border-amber-500/30 bg-amber-500/10">
          <Ban className="mr-1 h-3 w-3" /> Blocked
        </Badge>
      );
    case "queued":
    case "analyzing":
    case "abbreviating":
      return (
        <Badge variant="secondary" className="font-mono text-[10px] uppercase text-primary border-primary/20 bg-primary/10">
          <Loader2 className="mr-1 h-3 w-3 animate-spin" /> {status}
        </Badge>
      );
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

export default function HistoryPage() {
  const search = useSearch();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [rerunningJobId, setRerunningJobId] = useState<number | null>(null);
  const { toast } = useToast();

  const { mutate: rerunJob } = useRegenerateJob({
    mutation: {
      onSuccess: (data) => {
        navigate(`/jobs/${data.id}`);
      },
      onError: (err: unknown) => {
        const msg = (err as { message?: string })?.message ?? "Could not re-run this job. Please try again.";
        toast({ title: "Re-run failed", description: msg, variant: "destructive" });
      },
      onSettled: () => {
        setRerunningJobId(null);
      },
    },
  });

  const { mutate: deleteJob, isPending: isDeleting } = useDeleteJob({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListJobsQueryKey() });
        queryClient.invalidateQueries({ queryKey: getGetJobStatsQueryKey() });
        setConfirmDeleteId(null);
      },
    },
  });

  const params = new URLSearchParams(search);
  const subscriptionIdParam = params.get("subscriptionId");
  const parsedSubscriptionId = subscriptionIdParam ? parseInt(subscriptionIdParam, 10) : undefined;
  const activeSubscriptionId = parsedSubscriptionId !== undefined && !Number.isNaN(parsedSubscriptionId)
    ? parsedSubscriptionId
    : undefined;

  const listJobsParams = activeSubscriptionId !== undefined ? { subscriptionId: activeSubscriptionId } : undefined;

  const { data: jobs, isLoading: jobsLoading } = useListJobs(
    listJobsParams,
    {
      query: {
        queryKey: getListJobsQueryKey(listJobsParams),
        refetchInterval: (query) => {
          const data = query.state.data;
          if (!data) return false;
          const hasActive = (data as { status: string; videoStatus?: string }[]).some(
            (j) => isActiveStatus(j.status) || j.videoStatus === "rendering"
          );
          return hasActive ? 3000 : false;
        },
      },
    }
  );
  const hasActiveJobs = jobs?.some((j) => isActiveStatus(j.status)) ?? false;

  const statsParams = activeSubscriptionId !== undefined ? { subscriptionId: activeSubscriptionId } : undefined;

  const { data: stats, isLoading: statsLoading } = useGetJobStats(statsParams, {
    query: {
      queryKey: getGetJobStatsQueryKey(statsParams),
      refetchInterval: hasActiveJobs ? 5000 : false,
    },
  });

  const rerunInfoById = (() => {
    const result = new Map<number, { version: number; total: number; isLatest: boolean }>();
    if (!jobs) return result;
    const byEpisode = new Map<string, typeof jobs>();
    for (const job of jobs) {
      if (!job.rssEpisodeUrl) continue;
      const arr = byEpisode.get(job.rssEpisodeUrl) ?? [];
      arr.push(job);
      byEpisode.set(job.rssEpisodeUrl, arr);
    }
    for (const group of byEpisode.values()) {
      if (group.length < 2) continue;
      const sorted = [...group].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
      sorted.forEach((j, idx) => {
        result.set(j.id, {
          version: idx + 1,
          total: sorted.length,
          isLatest: idx === sorted.length - 1,
        });
      });
    }
    return result;
  })();

  const backfillGroups = (() => {
    if (!jobs) return [];
    const bySubscription = new Map<number, typeof jobs>();
    for (const job of jobs) {
      if (job.subscriptionId == null) continue;
      if (!bySubscription.has(job.subscriptionId)) {
        bySubscription.set(job.subscriptionId, []);
      }
      bySubscription.get(job.subscriptionId)!.push(job);
    }
    const groups: { subscriptionId: number; feedTitle: string; done: number; total: number }[] = [];
    for (const [subId, subJobs] of bySubscription) {
      const activeJobs = subJobs.filter((j) => isActiveStatus(j.status));
      if (activeJobs.length === 0) continue;
      const earliestActiveTs = Math.min(
        ...activeJobs.map((j) => new Date(j.createdAt).getTime())
      );
      const cohort = subJobs.filter(
        (j) => Math.abs(new Date(j.createdAt).getTime() - earliestActiveTs) <= BACKFILL_COHORT_WINDOW_MS
      );
      const done = cohort.filter((j) => !isActiveStatus(j.status)).length;
      const total = cohort.length;
      const feedTitle = subJobs.find((j) => j.feedTitle)?.feedTitle ?? `Feed #${subId}`;
      groups.push({ subscriptionId: subId, feedTitle, done, total });
    }
    return groups;
  })();

  const filterFeedTitle =
    activeSubscriptionId !== undefined && jobs && jobs.length > 0
      ? jobs.find((j) => j.subscriptionId === activeSubscriptionId)?.feedTitle ?? `Feed #${activeSubscriptionId}`
      : activeSubscriptionId !== undefined
      ? `Feed #${activeSubscriptionId}`
      : null;

  function setFilter(subscriptionId: number) {
    const next = new URLSearchParams(search);
    next.set("subscriptionId", String(subscriptionId));
    navigate(`/jobs?${next.toString()}`);
  }

  function clearFilter() {
    navigate("/jobs");
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div>
        <h1 className="text-3xl font-mono font-bold tracking-tight">Job History</h1>
        <p className="text-muted-foreground mt-2 text-sm">Review past processing jobs and pipeline metrics.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card/50 border-border/50">
          <CardContent className="p-4 flex flex-col gap-1">
            <span className="text-xs font-mono uppercase text-muted-foreground flex items-center gap-1"><HardDrive size={12} /> Total Jobs</span>
            <span className="text-2xl font-mono font-bold">{statsLoading ? "--" : stats?.totalJobs}</span>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border/50">
          <CardContent className="p-4 flex flex-col gap-1">
            <span className="text-xs font-mono uppercase text-muted-foreground flex items-center gap-1"><CheckCircle2 size={12} /> Completed</span>
            <span className="text-2xl font-mono font-bold text-emerald-500">{statsLoading ? "--" : stats?.completedJobs}</span>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border/50">
          <CardContent className="p-4 flex flex-col gap-1">
            <span className="text-xs font-mono uppercase text-muted-foreground flex items-center gap-1"><Clock size={12} /> Minutes Saved</span>
            <span className="text-2xl font-mono font-bold text-primary">{statsLoading ? "--" : Math.round(stats?.totalMinutesSaved || 0)}</span>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border/50">
          <CardContent className="p-4 flex flex-col gap-1">
            <span className="text-xs font-mono uppercase text-muted-foreground flex items-center gap-1"><Scissors size={12} /> Avg Compression</span>
            <span className="text-2xl font-mono font-bold text-primary">{statsLoading ? "--" : `${Math.round(stats?.averageCompressionPercent || 0)}%`}</span>
          </CardContent>
        </Card>
      </div>

      <div className="bg-card/30 border border-border/50 rounded-lg overflow-hidden backdrop-blur-sm">
        {activeSubscriptionId !== undefined && (
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/50 bg-primary/5">
            <Rss size={13} className="text-primary shrink-0" />
            <span className="text-xs font-mono text-primary flex-1 truncate">
              Filtering by: <span className="font-semibold">{filterFeedTitle ?? `Feed #${activeSubscriptionId}`}</span>
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 shrink-0 text-primary hover:text-primary hover:bg-primary/10"
              onClick={clearFilter}
              title="Clear filter"
            >
              <X size={12} />
            </Button>
          </div>
        )}

        {backfillGroups.length > 0 && (
          <div className="border-b border-border/50">
            {backfillGroups.map((group) => (
              <div
                key={group.subscriptionId}
                className="flex items-center gap-3 px-4 py-2.5 bg-primary/5"
              >
                <Loader2 size={13} className="text-primary shrink-0 animate-spin" />
                <span className="text-xs font-mono text-primary flex-1 truncate">
                  <span className="font-semibold">{group.feedTitle}</span>
                  {" — "}
                  <span>
                    {group.done} of {group.total} episode{group.total !== 1 ? "s" : ""} processed
                  </span>
                </span>
                <div className="flex items-center gap-1.5 shrink-0">
                  {Array.from({ length: group.total }).map((_, idx) => (
                    <div
                      key={idx}
                      className={`h-1.5 w-4 rounded-full transition-colors duration-500 ${
                        idx < group.done ? "bg-primary" : "bg-primary/20"
                      }`}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-12 gap-2 p-4 border-b border-border/50 bg-secondary/30 text-xs font-mono uppercase tracking-wider text-muted-foreground">
          <div className="col-span-4 md:col-span-4">Source</div>
          <div className="col-span-2 hidden md:block">Mode</div>
          <div className="col-span-3 md:col-span-2">Duration</div>
          <div className="col-span-2 hidden md:block">Date</div>
          <div className="col-span-3 md:col-span-2 text-right">Status</div>
        </div>

        {jobsLoading ? (
          <div className="p-8 text-center text-muted-foreground font-mono text-sm flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading your history...
          </div>
        ) : jobs?.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <BarChart3 className="mx-auto h-8 w-8 mb-3 opacity-20" />
            <p className="font-mono text-sm mb-4">
              {activeSubscriptionId !== undefined
                ? "No episodes from this feed yet."
                : "No abbreviations yet."}
            </p>
            {activeSubscriptionId === undefined && (
              <Link href="/upload">
                <Button size="sm" className="font-mono uppercase text-xs tracking-wider">
                  New Job
                </Button>
              </Link>
            )}
          </div>
        ) : (
          <div className="divide-y divide-border/30">
            {jobs?.map((job, i) => {
              const expired = isExpired(job.expiresAt);
              const rerun = rerunInfoById.get(job.id);
              return (
                <div
                  key={job.id}
                  className={`grid grid-cols-12 gap-2 p-4 items-center hover:bg-secondary/40 transition-colors group animate-in slide-in-from-bottom-2 fade-in ${
                    rerun
                      ? rerun.isLatest
                        ? "border-l-2 border-l-primary/60"
                        : "border-l-2 border-l-primary/20 bg-secondary/10"
                      : ""
                  }`}
                  style={{ animationDelay: `${i * 50}ms`, animationFillMode: "both" }}
                  data-testid={rerun ? `rerun-row-v${rerun.version}-of-${rerun.total}-job-${job.id}` : undefined}
                >
                  <div
                    className="col-span-4 md:col-span-4 flex items-center gap-3 overflow-hidden cursor-pointer"
                    onClick={() => navigate(`/jobs/${job.id}`)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && navigate(`/jobs/${job.id}`)}
                  >
                    <div className="h-8 w-8 rounded bg-secondary flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                      {job.sourceType === "rss" ? (
                        <Rss className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                      ) : (
                        <FileAudio className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                      )}
                    </div>
                    <div className="min-w-0 flex flex-col overflow-hidden">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className={`font-medium text-sm truncate ${rerun && !rerun.isLatest ? "text-muted-foreground" : ""}`}>{job.originalFilename}</span>
                        {rerun && (
                          <Badge
                            variant="outline"
                            className={`shrink-0 font-mono text-[9px] uppercase px-1.5 py-0 h-4 ${
                              rerun.isLatest
                                ? "border-primary/40 bg-primary/10 text-primary"
                                : "border-muted-foreground/30 text-muted-foreground"
                            }`}
                            title={
                              rerun.isLatest
                                ? `Latest of ${rerun.total} attempts on this episode`
                                : `Earlier attempt (v${rerun.version} of ${rerun.total}) on this episode`
                            }
                          >
                            <RefreshCw className="mr-0.5 h-2.5 w-2.5" />
                            v{rerun.version}
                            {rerun.isLatest ? " · latest" : ""}
                          </Badge>
                        )}
                      </div>
                      {job.subscriptionId != null ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFilter(job.subscriptionId!);
                          }}
                          className="inline-flex items-center gap-1 text-[10px] font-mono text-primary/70 hover:text-primary transition-colors w-fit"
                          title="Filter by this feed"
                        >
                          <Rss size={9} /> {job.feedTitle ?? "Feed subscription"}
                        </button>
                      ) : job.status === "blocked" ? (
                        <span className="text-[10px] text-amber-500/80 font-mono">Upgrade required to process</span>
                      ) : null}
                    </div>
                  </div>

                  <div className="col-span-2 hidden md:flex items-center gap-1 text-xs font-mono text-muted-foreground">
                    {modeIcon(job.mode)}
                    <span>{modeLabel(job.mode)}</span>
                    <span className="opacity-40">·{job.targetPercent}%</span>
                  </div>

                  <div className="col-span-3 md:col-span-2 flex flex-col text-xs font-mono text-muted-foreground">
                    {job.status === "ready" ? (
                      <div className="flex items-center gap-1">
                        <span className="text-foreground line-through opacity-50">{formatDuration(job.originalDurationSeconds)}</span>
                        <span>→</span>
                        <span className={`font-bold ${expired ? "text-muted-foreground" : "text-primary"}`}>{formatDuration(job.outputDurationSeconds)}</span>
                      </div>
                    ) : (
                      <span>--:--</span>
                    )}
                  </div>

                  <div className="col-span-2 hidden md:block text-xs text-muted-foreground font-mono">
                    {format(new Date(job.createdAt), "MMM dd, HH:mm")}
                  </div>

                  <div className="col-span-3 md:col-span-2 flex items-center justify-end gap-2">
                    {job.status === "queued" &&
                      Date.now() - new Date(job.createdAt).getTime() > QUEUED_WARN_MS && (
                        <span
                          title="This job has been waiting a while. It may be queued behind another job. If it stays here, try again or contact support."
                          className="shrink-0 text-amber-500 cursor-help"
                          aria-label="Job may be stuck in queue"
                        >
                          <AlertTriangle className="h-3.5 w-3.5" />
                        </span>
                      )}
                    {job.status === "ready" && job.outputUrl && !expired && (
                      <Button
                        asChild
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <a href={job.outputUrl} download title="Download">
                          <Download className="h-3.5 w-3.5" />
                        </a>
                      </Button>
                    )}
                    {job.videoStatus === "rendering" && (
                      <Badge
                        variant="secondary"
                        className="font-mono text-[10px] uppercase text-violet-400 border-violet-400/20 bg-violet-400/10 shrink-0"
                        title="Video render in progress"
                      >
                        {job.videoRenderProgress !== null && job.videoRenderProgress !== undefined ? (
                          <><Video className="mr-1 h-3 w-3" />Rendering {Math.round(job.videoRenderProgress)}%</>
                        ) : (
                          <><Loader2 className="mr-1 h-3 w-3 animate-spin" />Rendering</>
                        )}
                      </Badge>
                    )}
                    {getStatusBadge(job.status, job.expiresAt)}
                    {job.status !== "blocked" && (
                      <Link href={`/jobs/${job.id}`}>
                        <PlayCircle className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </Link>
                    )}
                    {job.status === "ready" && job.sourceType === "rss" && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-primary"
                        title="Re-run abbreviation"
                        data-testid={`button-rerun-${job.id}`}
                        disabled={rerunningJobId === job.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setRerunningJobId(job.id);
                          rerunJob({ id: job.id, data: { mode: (job.mode ?? "full_recap") as "key_quotes" | "quick_catchup" | "full_recap", targetPercent: job.targetPercent ?? 40 } });
                        }}
                      >
                        {rerunningJobId === job.id
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <RefreshCw className="h-3.5 w-3.5" />}
                      </Button>
                    )}
                    {!["analyzing", "abbreviating"].includes(job.status) && (
                      confirmDeleteId === job.id ? (
                        <span className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                            disabled={isDeleting}
                            title="Confirm delete"
                            onClick={(e) => { e.stopPropagation(); deleteJob({ id: job.id }); }}
                          >
                            {isDeleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            title="Cancel"
                            onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); }}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </span>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                          title="Delete job"
                          onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(job.id); }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
